import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import CriteriaDetail from '../components/CriteriaDetail';

// Bot-run createdAt arrives as "MM/DD/YYYY,THH:mm:ss-7" — already Pacific, so
// just reshape it rather than re-parsing it as a real timestamp.
function fmtStarted(raw) {
  if (!raw) return '';
  const m = String(raw).match(/^(\d{2})\/(\d{2})\/(\d{4}),T(\d{2}:\d{2}:\d{2})/);
  return m ? `${m[2]}/${m[1]} ${m[4]}` : String(raw);
}

// The six-column table needs ~560px; anything narrower gets the stacked layout.
// matchMedia (not a one-time width read) so rotating the phone re-lays out.
const NARROW = '(max-width: 680px)';

function useNarrow() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(NARROW);
    const onChange = e => setNarrow(e.matches);
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);
  return narrow;
}

// Live view of the booking bot's current runs, scoped server-side to the
// client's own logins plus any accounts they manage. Hidden entirely when
// nothing is running so it never takes up space on a quiet day.
export default function PortalActiveRuns() {
  const [runs, setRuns] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState({});
  const narrow = useNarrow();

  const load = useCallback(() => {
    api.getPortalActiveRuns()
      .then(rows => { setRuns(rows); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // keep "what's running" actually current
    return () => clearInterval(id);
  }, [load]);

  if (!loaded || runs.length === 0) return null;

  const toggle = id => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="card" style={{ marginBottom: 12, borderLeft: '3px solid #38a169' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%', background: '#38a169',
          display: 'inline-block', flexShrink: 0
        }} />
        <strong style={{ fontSize: 14 }}>
          Running Now ({runs.length})
        </strong>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={load}>
          Refresh
        </button>
      </div>

      {narrow
        ? <RunCards runs={runs} expanded={expanded} onToggle={toggle} />
        : <RunTable runs={runs} expanded={expanded} onToggle={toggle} />}
    </div>
  );
}

// Phone layout: one block per run, everything wraps, nothing scrolls sideways.
function RunCards({ runs, expanded, onToggle }) {
  return (
    <div className="run-cards">
      {runs.map(r => {
        const id = String(r._id);
        const isOpen = !!expanded[id];
        const count = (r.shiftCriteria || []).length;
        return (
          <div key={id} className="run-card">
            <div className="run-card-head">
              <span className="run-card-prop">{r.clientID}</span>
              <span className="run-card-started">{fmtStarted(r.createdAt) || '—'}</span>
            </div>
            <div className="run-card-user">{r.userName}</div>

            <dl className="run-card-fields">
              <dt>Search Time</dt>
              <dd style={{ fontFamily: 'monospace' }}>{r.searchTime || '—'}</dd>
              <dt>Booking Window</dt>
              <dd>{r.start && r.end ? `${r.start} – ${r.end}` : '—'}</dd>
            </dl>

            {count === 0 ? (
              <div className="run-card-notargets">No targets</div>
            ) : (
              <>
                <button
                  className="btn btn-secondary run-card-targets"
                  onClick={() => onToggle(id)}
                >
                  {isOpen ? '▾' : '▸'} {count} target{count === 1 ? '' : 's'}
                </button>
                {isOpen && (
                  <div style={{ marginTop: 8 }}>
                    <CriteriaDetail criteria={r.shiftCriteria} stacked />
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Desktop / landscape layout: unchanged table.
function RunTable({ runs, expanded, onToggle }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ minWidth: 560 }}>
        <thead>
          <tr>
            <th>Property</th>
            <th>Username</th>
            <th>Search Time</th>
            <th>Booking Window</th>
            <th>Targets</th>
            <th style={{ whiteSpace: 'nowrap' }}>Started</th>
          </tr>
        </thead>
        <tbody>
          {runs.map(r => {
            const id = String(r._id);
            const isOpen = !!expanded[id];
            const count = (r.shiftCriteria || []).length;
            return (
              <React.Fragment key={id}>
                <tr>
                  <td>{r.clientID}</td>
                  <td>{r.userName}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.searchTime || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#718096' }}>
                    {r.start && r.end ? `${r.start} – ${r.end}` : '—'}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {count === 0
                      ? <span style={{ color: '#a0aec0' }}>0</span>
                      : (
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={() => onToggle(id)}
                        >
                          {isOpen ? '▾' : '▸'} {count}
                        </button>
                      )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: '#718096' }}>
                    {fmtStarted(r.createdAt) || '—'}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={6} style={{ padding: '4px 12px 12px 24px' }}>
                      <CriteriaDetail criteria={r.shiftCriteria} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
